import React, { ReactElement } from 'react';
import { ellipsis, emptyObject } from '@fabric-ui/utils';
import { Box, SxProps, Theme } from '@mui/material';
import { SeverityBGColors } from './Severity';

export type AvTagVariant =
  | 'teal'
  | 'neutral'
  | 'primary'
  | 'red'
  | 'green'
  | 'orange'
  | 'yellow'
  | 'purple'
  | 'ai'
  | 'none'
  | 'low'
  | 'medium'
  | 'high'
  | 'critical';

interface IAvTagProps {
  text: ReactElement | string | number;
  variant?: AvTagVariant;
  isActive?: boolean;
  oneLine?: boolean;
  sx?: SxProps<Theme>;
  isArrowRight?: boolean;
  isArrowLeft?: boolean;
}

const variantStyles: Record<AvTagVariant, { bg: string; color: string; border: string }> = {
  teal: {
    bg: 'var(--color-surface-accent-teal-primary)',
    color: 'var(--color-content-accent-teal-default)',
    border: 'var(--color-border-accent-teal-primary)',
  },
  neutral: {
    bg: 'var(--color-surface-status-neutral-primary)',
    color: 'var(--color-content-status-neutral-primary)',
    border: 'var(--color-border-status-neutral-default)',
  },
  primary: {
    bg: 'var(--color-surface-accent-blue-primary)',
    color: 'var(--color-content-accent-blue-default)',
    border: 'var(--color-border-accent-blue-primary)',
  },
  red: {
    bg: 'var(--color-surface-accent-red-primary)',
    color: 'var(--color-content-accent-red-default)',
    border: 'var(--color-border-accent-red-primary)',
  },
  green: {
    bg: 'var(--color-surface-accent-green-primary)',
    color: 'var(--color-content-accent-green-default)',
    border: 'var(--color-border-accent-green-primary)',
  },
  orange: {
    bg: 'var(--color-surface-accent-orange-primary)',
    color: 'var(--color-content-accent-orange-default)',
    border: 'var(--color-border-accent-orange-primary)',
  },
  yellow: {
    bg: 'var(--color-surface-accent-yellow-primary)',
    color: 'var(--color-content-accent-yellow-default)',
    border: 'var(--color-border-accent-yellow-primary)',
  },
  purple: {
    bg: 'var(--color-surface-accent-purple-primary)',
    color: 'var(--color-content-accent-purple-default)',
    border: 'var(--color-border-accent-purple-primary)',
  },
  ai: {
    bg: 'linear-gradient(90deg, color-mix(in srgb, var(--dataviz-color-categorical-primary-chart02, #cb65eb) 65%, transparent) 0%, color-mix(in srgb, var(--dataviz-color-categorical-primary-chart01, #346ff8) 65%, transparent) 100%)',
    color: 'var(--color-content-accent-deepPurple-default)',
    border: 'var(--color-border-accent-deepPurple-primary)',
  },
  none: {
    bg: SeverityBGColors.NONE,
    color: 'var(--color-content-severity-none-primary)',
    border: 'var(--color-border-severity-none-primary)',
  },
  low: {
    bg: SeverityBGColors.LOW,
    color: 'var(--color-content-severity-low-primary)',
    border: 'var(--color-border-severity-low-primary)',
  },
  medium: {
    bg: SeverityBGColors.MEDIUM,
    color: 'var(--color-content-severity-medium-primary)',
    border: 'var(--color-border-severity-medium-primary)',
  },
  high: {
    bg: SeverityBGColors.HIGH,
    color: 'var(--color-content-severity-high-primary)',
    border: 'var(--color-border-severity-high-primary)',
  },
  critical: {
    bg: SeverityBGColors.CRITICAL,
    color: 'var(--color-content-severity-critical-primary)',
    border: 'var(--color-border-severity-critical-primary)',
  },
};

function arrowStyle(dir: 'after' | 'before', bgColor: string, borderColor: string): SxProps<Theme> {
  const isRight = dir === 'after';
  const hasBorder = borderColor !== 'transparent';
  const borderDir = isRight ? 'before' : 'after'; // Use opposite pseudo-element for border

  return {
    position: 'relative',
    // Border-colored arrow (background layer) - only if border exists
    ...(hasBorder && {
      [`&::${borderDir}`]: {
        content: "''",
        display: 'inline-block',
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        zIndex: 0,
        ...(isRight
          ? {
              right: -9,
              borderTop: '13px solid transparent',
              borderBottom: '13px solid transparent',
              borderLeft: `9px solid ${borderColor}`,
            }
          : {
              left: -9,
              borderTop: '13px solid transparent',
              borderBottom: '13px solid transparent',
              borderRight: `9px solid ${borderColor}`,
            }),
      },
    }),
    // Background-colored arrow (foreground layer)
    [`&::${dir}`]: {
      content: "''",
      display: 'inline-block',
      position: 'absolute',
      top: '50%',
      transform: 'translateY(-50%)',
      zIndex: 1,
      ...(isRight
        ? {
            right: -8,
            borderTop: '12px solid transparent',
            borderBottom: '12px solid transparent',
            borderLeft: `8px solid ${bgColor}`,
          }
        : {
            left: -8,
            borderTop: '12px solid transparent',
            borderBottom: '12px solid transparent',
            borderRight: `8px solid ${bgColor}`,
          }),
    },
  };
}

const AvTag: React.FC<IAvTagProps> = ({
  text,
  variant = 'neutral',
  isActive = false,
  oneLine = false,
  sx = emptyObject,
  isArrowRight = false,
  isArrowLeft = false,
}) => {
  const { bg: backgroundColor, color: labelColor, border } = variantStyles[isActive ? 'primary' : variant];

  const getArrowStyle = () => {
    if (isArrowLeft) {
      return arrowStyle('before', backgroundColor, border);
    }
    if (isArrowRight) {
      return arrowStyle('after', backgroundColor, border);
    }
    return undefined;
  };

  const baseStyles: SxProps<Theme> = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    whiteSpace: 'pre-wrap',
    ...(oneLine ? ellipsis : {}),
    padding: '2px 6px',
    borderRadius: '8px',
    lineHeight: '18px',
    fontWeight: 500,
    fontSize: 13,
    color: labelColor,
    background: backgroundColor,
    border: border !== 'transparent' ? `1px solid ${border}` : 'none',
    ...(isArrowRight && {
      borderBottomRightRadius: 0,
      borderTopRightRadius: 0,
      paddingLeft: '10px',
      marginRight: '10px', // For arrow space
    }),
    ...(isArrowLeft && {
      borderBottomLeftRadius: 0,
      borderTopLeftRadius: 0,
      paddingRight: '10px',
      marginLeft: '10px', // For arrow space
    }),
    ...getArrowStyle(),
    ...sx,
  };

  if (!text) {
    return null;
  }

  return (
    <Box component="span" sx={baseStyles}>
      {text}
    </Box>
  );
};

export { AvTag };
